import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription, combineLatest } from 'rxjs';
import { map, catchError, switchMap, finalize, filter } from 'rxjs/operators';
import { UserModel } from '../_models/user.model';
import { AuthModel } from '../_models/auth.model';

import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { CurrentUserDto } from '@abp/ng.core';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { Webshops } from '../../webshop/store/models/webshops';
import { LoginsService } from '../../webshop/webshops/logins/shared/logins.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `kkisui_2`;

  // public fields
  currentUser$: Observable<Webshops.ILoggedInUser>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<Webshops.ILoggedInUser>;
  isLoadingSubject: BehaviorSubject<boolean>;

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  get currentUserValue(): Webshops.ILoggedInUser {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: Webshops.ILoggedInUser) {
    this.currentUserSubject.next(user);
  }

  constructor(
    
    private loginService:LoginsService,
    private router: Router,
    private oauthService:OAuthService) 
    {
      
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<Webshops.ILoggedInUser>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    //const subscr = this.getUserByToken().subscribe();
    //this.unsubscribe.push(subscr);


    const userRefresh=this.isAuthenticated$.subscribe(x=>
      {
        if(x && this.hasValidToken())
        {
            const sc2 = this.getUserByToken().subscribe();
            this.unsubscribe.push(sc2);
        }
        else
        {
          this.currentUserSubject.next(null);
        }
      });
      this.unsubscribe.push(userRefresh);

    oauthService.configure(environment.oAuthConfig);

    this.oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        console.error('OAuthErrorEvent Object:', event);
      } else {
        console.warn('OAuthEvent Object:', event);
      }
  });

  // This is tricky, as it might cause race conditions (where access_token is set in another
    // tab before everything is said and done there.
    // TODO: Improve this setup. See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
    window.addEventListener('storage', (event) => {
      // The `key` is `null` if the event was caused by `.clear()`
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }

      console.warn('Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

      if (!this.oauthService.hasValidAccessToken()) {
        this.navigateToLoginPage();
      }
    });

    this.oauthService.events
      .subscribe(_ => {
        this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
      });

    this.oauthService.events
      .pipe(filter(e => ['token_received'].includes(e.type)))
      .subscribe(e => this.oauthService.loadUserProfile());

    this.oauthService.events
      .pipe(filter(e => ['session_terminated', 'session_error'].includes(e.type)))
      .subscribe(e => this.navigateToLoginPage());

    this.oauthService.setupAutomaticSilentRefresh();
}

public runInitialLoginSequence(): Promise<void> {
  console.log("INIT LOGIN");
  if (location.hash) {
    console.log('Encountered hash fragment, plotting as table...');
    console.table(location.hash.substr(1).split('&').map(kvp => kvp.split('=')));
  }

  // 0. LOAD CONFIG:
  // First we have to check to see how the IdServer is
  // currently configured:
  return this.oauthService.loadDiscoveryDocument()

    // For demo purposes, we pretend the previous call was very slow
    //.then(() => new Promise<void>(resolve => setTimeout(() => resolve(), 1500)))

    // 1. HASH LOGIN:
    // Try to log in via hash fragment after redirect back
    // from IdServer from initImplicitFlow:
    .then(() => this.oauthService.tryLogin())

    .then(() => {
      if (this.oauthService.hasValidAccessToken()) {
        return Promise.resolve();
      }

      // 2. SILENT LOGIN:
      // Try to log in via a refresh because then we can prevent
      // needing to redirect the user:
      return this.oauthService.silentRefresh()
        .then(() => Promise.resolve())
        .catch(result => {
          // Subset of situations from https://openid.net/specs/openid-connect-core-1_0.html#AuthError
          // Only the ones where it's reasonably sure that sending the
          // user to the IdServer will help.
          const errorResponsesRequiringUserInteraction = [
            'interaction_required',
            'login_required',
            'account_selection_required',
            'consent_required','code_error'
          ];

          if (result
            && result.reason
            && errorResponsesRequiringUserInteraction.indexOf(result.reason.error) >= 0) {

            // 3. ASK FOR LOGIN:
            // At this point we know for sure that we have to ask the
            // user to log in, so we redirect them to the IdServer to
            // enter credentials.
            //
            // Enable this to ALWAYS force a user to login.
            // this.login();
            //
            // Instead, we'll now do this:
            console.warn('User interaction is needed to log in, we will wait for the user to manually log in.');
            return Promise.resolve();
          }

          // We can't handle the truth, just pass on the problem to the
          // next handler.
          return Promise.reject(result);
        });
    })

    .then(() => {
      this.isLoadingSubject.next(false);

      // Check for the strings 'undefined' and 'null' just to be sure. Our current
      // login(...) should never have this, but in case someone ever calls
      // initImplicitFlow(undefined | null) this could happen.
      if (this.oauthService.state && this.oauthService.state !== 'undefined' && this.oauthService.state !== 'null') {
        let stateUrl = this.oauthService.state;
        if (stateUrl.startsWith('/') === false) {
          stateUrl = decodeURIComponent(stateUrl);
        }
        console.log(`There was state of ${this.oauthService.state}, so we are sending you to: ${stateUrl}`);
        this.router.navigateByUrl(stateUrl);
      }
    })
    .catch(() => this.isLoadingSubject.next(false));
}

  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    //this.isDoneLoading$
  ]).pipe(map(values => values.every(b => b)));

  private navigateToLoginPage() {
    // TODO: Remember current URL
    this.router.navigateByUrl('/should-login');
  }

  login()
  {
     // call encodeURIComponent on the argument to the method.
     this.oauthService.initLoginFlow( this.router.url);
  }

  logout() {
    this.oauthService.logOut();
  }

  refresh()
  {
    this.oauthService.silentRefresh();
  }

  public hasValidToken() { return this.oauthService.hasValidAccessToken(); }

  getUserByToken(): Observable<Webshops.ILoggedInUser> {
    
    
    this.isLoadingSubject.next(true);
    return this.loginService.getUser().pipe(
      map((user: Webshops.ILoggedInUser) => {
    
        if (user) {
          this.currentUserSubject.next(user);
        } else {
          this.currentUserSubject.next(null);
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
    
  }


  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
